/**
 * Core utility methods for localStorage operations
 * Provides type-safe, error-handled access to browser localStorage
 */

/**
 * Check if localStorage is available in the current environment
 */
const isAvailable = (): boolean => {
  try {
    const test = '__localStorage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch {
    return false;
  }
};

/**
 * Get an item from localStorage
 * @param key - The key to retrieve
 * @returns The parsed value or null if not found or on error
 */
  export const getItem = <T = unknown>(key: string): T | null => {
  if (!isAvailable()) {
    console.warn('localStorage is not available');
    return null;
  }

  try {
    const item = localStorage.getItem(key);
    if (item === null) {
      return null;
    }
    return JSON.parse(item) as T;
  } catch (error) {
    console.error(`Error getting item from localStorage with key "${key}":`, error);
    return null;
  }
};

/**
 * Set an item in localStorage
 * @param key - The key to store the value under
 * @param value - The value to store (will be JSON stringified)
 * @returns true if successful, false otherwise
 */
export const setItem = <T = unknown>(key: string, value: T): boolean => {
  if (!isAvailable()) {
    console.warn('localStorage is not available');
    return false;
  }

  try {
    const serialized = JSON.stringify(value);
    localStorage.setItem(key, serialized);
    return true;
  } catch (error) {
    console.error(`Error setting item in localStorage with key "${key}":`, error);
    return false;
  }
};

/**
 * Remove an item from localStorage
 * @param key - The key to remove
 * @returns true if successful, false otherwise
 */
export const removeItem = (key: string): boolean => {
  if (!isAvailable()) {
    console.warn('localStorage is not available');
    return false;
  }

  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error(`Error removing item from localStorage with key "${key}":`, error);
    return false;
  }
};

/**
 * Clear all items from localStorage
 * @returns true if successful, false otherwise
 */
export const clear = (): boolean => {
  if (!isAvailable()) {
    console.warn('localStorage is not available');
    return false;
  }

  try {
    localStorage.clear();
    return true;
  } catch (error) {
    console.error('Error clearing localStorage:', error);
    return false;
  }
};

/**
 * Check if a key exists in localStorage
 * @param key - The key to check
 * @returns true if the key exists, false otherwise
 */
export const hasItem = (key: string): boolean => {
  if (!isAvailable()) {
    return false;
  }

  try {
    return localStorage.getItem(key) !== null;
  } catch (error) {
    console.error(`Error checking item in localStorage with key "${key}":`, error);
    return false;
  }
};

/**
 * Get all keys from localStorage
 * @returns Array of all keys, or empty array on error
 */
export const getAllKeys = (): string[] => {
  if (!isAvailable()) {
    return [];
  }

  try {
    return Object.keys(localStorage);
  } catch (error) {
    console.error('Error getting all keys from localStorage:', error);
    return [];
  }
};

/**
 * Get the number of items in localStorage
 * @returns The number of items, or 0 on error
 */
export const getLength = (): number => {
  if (!isAvailable()) {
    return 0;
  }

  try {
    return localStorage.length;
  } catch (error) {
    console.error('Error getting localStorage length:', error);
    return 0;
  }
};

export function setLocalStorageWithExpiry(key: string, value: string | boolean, days: number) {
  const now = new Date()
  const item = {
      value: typeof value === 'string' ? value : value.toString(),
      expiry: now.getTime() + days * 24 * 60 * 60 * 1000,
  }
  setItem(key, item)
}

export function getLocalStorageWithExpiry(key: string) {
  const item = getItem(key)
  if (!item) return null

  const now = new Date()
  if (now.getTime() > (item as { expiry: number }).expiry) {
      removeItem(key)
      return null
  } else {
      return typeof (item as { value: string | boolean }).value === 'string' ? (item as { value: string | boolean }).value : Boolean((item as { value: string | boolean }).value)
  }
}

/**
 * localStorage utility object with all methods
 */
export const localStorageUtil = {
  getItem,
  setItem,
  removeItem: removeItem,
  clear: clear,
  hasItem: hasItem,
  getAllKeys: getAllKeys,
  getLength: getLength,
  isAvailable: isAvailable,
  setLocalStorageWithExpiry: setLocalStorageWithExpiry,
  getLocalStorageWithExpiry: getLocalStorageWithExpiry,
};

export default localStorageUtil;

